import React from 'react';
import { List, ListItem, Separator, Tooltip, TooltipToggle } from 'telia-front-react';
import { TranslationsContext } from '@teliaee/telia-translate';
import { formatPrice } from '../../util/PriceUtil';
import { Option } from '../form/PackageOptionCardGroup';
import { Package } from '../MobileBusinessInterfaces';

interface Props {
  mobilePackage: Package;
  packageInfoClass?: string;
  priceItemClass?: string;
  offerPriceItemClass?: string;
}

export const offerPriceExists = (option: Option | Package) => {
  return option?.offerPrice !== null && option?.offerPrice !== undefined;
};

export const employeeOfferPriceExists = (option: Package) => {
  return option?.employeeOfferPrice !== null && option?.employeeOfferPrice !== undefined;
};

const renderRegularPrice = (option: Option | Package, translate: (key: string) => {}, priceUnit?: string) => {
  return (
    <>
      {`${translate('mobileBusiness.regularPrice')} `}
      <b>{`${formatPrice(option.price)} ${priceUnit || translate('mobileBusiness.eurPerMonth')}`}</b>
    </>
  );
};

export const renderRegularPriceWithTooltip = (option: Option | Package, tooltipId: string, translate: (key: string) => {}, priceUnit?: string) => {
  if (option?.tooltipContent) {
    return (
      <>
        <Tooltip
          referenceElement={
            <TooltipToggle id={tooltipId} className="text-small">
              {renderRegularPrice(option, translate, priceUnit)}
            </TooltipToggle>
          }
          placement="bottom"
        >
          {option.tooltipContent}
        </Tooltip>
      </>
    );
  }
  return renderRegularPrice(option, translate, priceUnit);
};

class PackagePrice extends React.Component<Props> {
  static contextType = TranslationsContext;
  declare context: React.ContextType<typeof TranslationsContext>;

  private getRegularPackagePrice() {
    const translate = this.context.translate;

    const { mobilePackage, priceItemClass, offerPriceItemClass } = this.props;
    const hasOfferPrice = offerPriceExists(mobilePackage);
    const tooltipId = `tooltip-toggle-mobile-business-package-${mobilePackage?.capacity}`;

    return (
      <List type="unstyled" verticalSpacing="none">
        <ListItem className={priceItemClass || 'h4'}>
          {`${formatPrice(hasOfferPrice ? mobilePackage?.offerPrice : mobilePackage?.price)} ${translate('packagePrice.eurPerMonth')}`}
        </ListItem>
        {hasOfferPrice && (
          <ListItem className={offerPriceItemClass || 'text-small'}>{renderRegularPriceWithTooltip(mobilePackage, tooltipId, translate)}</ListItem>
        )}
      </List>
    );
  }

  private getUnlimitedPlusPrice() {
    const translate = this.context.translate;

    const { mobilePackage, priceItemClass, packageInfoClass } = this.props;
    const hasOfferPrice = offerPriceExists(mobilePackage);
    const hasEmployeeOfferPrice = employeeOfferPriceExists(mobilePackage);

    return (
      <List type="unstyled" verticalSpacing="none">
        <ListItem className={packageInfoClass}>
          <p className="text-bold">{translate('mobileBusiness.premiumBasePrice')}</p>
        </ListItem>
        <ListItem className={priceItemClass || 'h4'}>
          {`${formatPrice(hasOfferPrice ? mobilePackage?.offerPrice : mobilePackage?.price)} ${translate('packagePrice.eurPerMonth')}`}
          <Separator spacing="xxsmall" color="invisible" />
        </ListItem>
        <ListItem>
          <p className="text-bold">{translate('mobileBusiness.premiumEmployeePrice')}</p>
        </ListItem>
        <ListItem className={priceItemClass || 'text-normal'}>
          {`${formatPrice(hasEmployeeOfferPrice ? mobilePackage?.employeeOfferPrice : mobilePackage?.employeePrice)} ${translate('packagePrice.eurPerMonth')}`}
        </ListItem>
      </List>
    );
  }

  public render() {
    const { mobilePackage } = this.props;

    return mobilePackage.isUnlimitedPlus ? this.getUnlimitedPlusPrice() : this.getRegularPackagePrice();
  }
}

export default PackagePrice;
